"use strict";
var __extends = (this && this.__extends) || (function () {
    var extendStatics = Object.setPrototypeOf ||
        ({ __proto__: [] } instanceof Array && function (d, b) { d.__proto__ = b; }) ||
        function (d, b) { for (var p in b) if (b.hasOwnProperty(p)) d[p] = b[p]; };
    return function (d, b) {
        extendStatics(d, b);
        function __() { this.constructor = d; }
        d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
    };
})();
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
/********************************************* CONFIG *********************************************/
var compareToHashedKey1 = "compareTo_llzh9z$";
var compareToHashedKey2 = "compareTo_unjduw$";
/***************************************** IMPORT MODULES *****************************************/
var util_1 = require("util");
var moment_1 = __importDefault(require("moment"));
var shared_1 = require("mad-utils/lib/shared");
var javelin_exports_1 = require("../javelin-exports");
/********************************************* EXPORT *********************************************/
/**
 * Javelin representation of a local date
 * JLocalDate implementation
 *
 * @author Andrew Faulkner & Julien Guerinet
 * @since 2.4.30
 */
var LocalDate = /** @class */ (function (_super) {
    __extends(LocalDate, _super);
    /*** INSTANTIATION ***/
    /**
     * Build new LocalDate using optional Moment or LocalDate object
     * Defaults to creating & using Moment object with current date & time
     *
     * @param {Moment|LocalDate} momentInstance Moment or LocalDate obj to build new LocalDate from
     * @constructor
     */
    function LocalDate(dateObj) {
        var _this = _super.call(this) || this;
        /**
         * If true, 'time' gets ignored in comparisons (true for ZonedDateTime, but not raw LocalDate)
         */
        _this.usesTime = false;
        /**
         * True if internal moment instance is valid.
         */
        _this.isValid = function () { return _this._moment.isValid(); };
        /**
         * Output displayed by node inspect - including REPL & terminal output
         */
        _this.inspect = function () { return "{ LocalDate ~ " + util_1.inspect(_this._moment) + " }"; };
        /**
         * Date as string in ISO format (from inner moment obj)
         * Example: "2017-11-27T17:34:12.129Z"
         * @return {string} current date in ISO format e.g. "2010-04-19T13:20:02.591Z"
         */
        _this.toISOString = function () { return _this._moment.toISOString(); };
        /**
         * Get current date as a Moment object (returns a clone to avoid mutation issues)
         * @return {Moment} close of current LocalDate object's internal Moment object
         */
        _this.toMoment = function () { return _this._moment.clone(); };
        /**
         * Returns date as string in "YYYY-MM-DD" format: e.g. "2010-11-18"
         * @return {string} date in "YYYY-MM-DD" format
         */
        _this.toYearMonthDateNumString = function () { return _this._moment.format("YYYY-MM-DD"); };
        /**
         * Returns date as string in "YYYY-MM-DD" format: e.g. "2010-11-18"
         * For use in JSON requests
         * @return {string} date in "YYYY-MM-DD" format
         */
        _this.toJson = function () { return _this.toYearMonthDateNumString(); };
        /*** STRING OUTPUTS ***/
        /**
         * toString behaviour for LocalDate instances
         */
        _this.toString = function () { return _this._moment.toString(); };
        /*** METHODS ***/
        /**
         * Create new LocalDate with given number of days added
         * @param days Number of days to add to the current date
         * @return Resulting [LocalDate]
         */
        _this.plusDays = function (days) {
            var newMoment = _this._moment.clone();
            newMoment.add(days, "days");
            return new LocalDate(newMoment);
        };
        /**
         * Create new LocalDate with given number of [weeks] added [NON-MUTATIVE]
         * @param weeks Number of weeks to add to the current date
         * @return Resulting [LocalDate]
         */
        _this.plusWeeks = function (weeks) {
            var newMoment = _this._moment.clone();
            newMoment.add(weeks, "weeks");
            return new LocalDate(newMoment);
        };
        /**
         * Create new LocalDate with given number of [months] added [NON-MUTATIVE]
         * @param months Number of months to add to the current date
         * @return Resulting [LocalDate]
         */
        _this.plusMonths = function (months) {
            var newMoment = _this._moment.clone();
            newMoment.add(months, "months");
            return new LocalDate(newMoment);
        };
        /**
         * Create new LocalDate with given number of [years] added [NON-MUTATIVE]
         * @param years Number of years to add to the current date
         * @return Resulting [LocalDate]
         */
        _this.plusYears = function (years) {
            var newMoment = _this._moment.clone();
            newMoment.add(years, "years");
            return new LocalDate(newMoment);
        };
        /**
         * Create new LocalDate with given number of [weeks] subtracted [NON-MUTATIVE]
         * @param weeks Number of weeks to subtract from the current date
         * @return Resulting [LocalDate]
         */
        _this.minusWeeks = function (weeks) {
            var newMoment = _this._moment.clone();
            newMoment.subtract(weeks, "weeks");
            return new LocalDate(newMoment);
        };
        /**
         * Create new LocalDate with given number of [years] subtracted [NON-MUTATIVE]
         * @param years Number of years to subtract from the current date
         * @return Resulting [LocalDate]
         */
        _this.minusYears = function (years) {
            var newMoment = _this._moment.clone();
            newMoment.subtract(years, "years");
            return new LocalDate(newMoment);
        };
        /*** COMPARISON ***/
        /**
         * Return true if this date is after given [date]
         * Technically nullable, but it throws if you don't provide a date
         */
        _this.isAfter = function (date) {
            if (typeof date === "undefined" || date == null) {
                throw new TypeError("isAfter must be provided with a date to compare against");
            }
            return _this._moment.isAfter(date._moment, _this.usesTime ? "second" : "day");
        };
        /**
         * Return true if this date is the same as given [date]
         * Technically nullable, but it throws if you don't provide a date
         */
        _this.isEqual = function (date) {
            if (typeof date === "undefined" || date == null) {
                throw new TypeError("isEqual must be provided with a date to compare against");
            }
            return _this._moment.isSame(date._moment, _this.usesTime ? "second" : "day");
        };
        /**
         * Return true if this date is before given [date]
         * Technically nullable, but it throws if you don't provide a date
         */
        _this.isBefore = function (date) {
            if (typeof date === "undefined" || date == null) {
                throw new TypeError("isBefore must be provided with a date to compare against");
            }
            return _this._moment.isBefore(date._moment, _this.usesTime ? "second" : "day");
        };
        /**
         * Returns true if this date is in the future & within given number of [weeks]
         */
        _this.isWithinWeeks = function (weeks) {
            var futureDate = moment_1.default().add(weeks, "weeks");
            return _this._moment.isBefore(futureDate, _this.usesTime ? "second" : "day") && _this.isFuture;
        };
        /**
         * TODO Ensure dateInWeeks rounding method is acceptable
         *
         * Note that this is imprecise - it rounds to the closest # of weeks
         * Returns number of weeks between the date and now {number}
         */
        _this.dateInWeeks = function () { return _this._moment.diff(moment_1.default(), "weeks") * -1; };
        /**
         * TODO Ensure dateInMonths rounding method is acceptable
         *
         * Note that this is imprecise - it 'floors' to the closest month
         * e.g. 27 days will still be 0 months.
         * Returns number of months between the date and now {number}
         */
        _this.dateInMonths = function () { return _this._moment.diff(moment_1.default(), "months") * -1; };
        /**
         * TODO Ensure dateInYears rounding method is acceptable
         *
         * Note that this is imprecise - it 'floors' to the closest year
         * Returns number of years between the date and now {number}
         */
        _this.dateInYears = function () { return _this._moment.diff(moment_1.default(), "years") * -1; };
        /**
         * Compare to LocalDate [date]; returns comparison index (-1, 0, 1)
         * Used in search functions
         */
        _this.compareTo = function (date) {
            var periodInterval = _this.usesTime ? "second" : "day";
            if (_this._moment.isBefore(date._moment, periodInterval))
                return -1;
            if (_this._moment.isAfter(date._moment, periodInterval))
                return 1;
            return 0;
        };
        /**
         * Return Unix timestamp (date in ms since Jan 1, 1970) as wrapped Number object
         *
         * Note: Must return as Number object, because Kotlin tries to operator
         * overload `-` against the output, resulting in num.subtract(someOtherNum),
         * which doesn't work without `num` being a `Number` wrapper object
         * i.e. in Kotlin (in this circumstance):
         *     someNumber - someOtherNumber [[someNumber minus someOtherNumber]]
         * ...transpiles to:
         *     someNumber.subtract(someOtherNumber)
         * someNumber must thus be an obj with a subtract method on it (Number is augmented)
         *
         * @returns {Number} date in milliseconds since January 1, 1970 (actual Number instance)
         */
        _this.toMillis = function () { return new Number(_this._moment.unix()); }; // tslint:disable-line:ban-types no-construct
        // TODO eliminate re-binding from the main localDate class
        // Re-binding - for some reason JVLNS requires this
        _this.localNow = function () { return new LocalDate(moment_1.default()); };
        _this.zonedNow = function () { return new ZonedDateTime(moment_1.default()); };
        _this.localOf = function (year, month, day) {
            var date = new LocalDate(moment_1.default(year + "-" + month + "-" + day, "YYYY-MM-DD"));
            if (date.isValid())
                return date;
            throw new Error("Invalid date given: year: " + year + "; month: " + month + "; day: " + day);
        };
        var date = dateObj || moment_1.default();
        Object.defineProperty(_this, "_moment", {
            configurable: false,
            enumerable: false,
            writable: false,
            value: moment_1.default.isMoment(date) ? date : date._moment.clone(),
        });
        return _this;
    }
    Object.defineProperty(LocalDate.prototype, "$string", {
        /**
         * Returns date in String format
         */
        get: function () {
            return this._moment.toString();
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(LocalDate.prototype, "isFuture", {
        /*** GETTERS ***/
        /**
         * Returns true if this date is in the future, false otherwise (today returns false)
         */
        get: function () {
            return this._moment.isAfter(moment_1.default(), this.usesTime ? "second" : "day");
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(LocalDate.prototype, "isPast", {
        /**
         * Returns true if this date is in the past, false otherwise (today returns false)
         */
        get: function () {
            return this._moment.isBefore(moment_1.default(), this.usesTime ? "second" : "day");
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(LocalDate.prototype, "isToday", {
        /**
         * Returns true if this date is today
         */
        get: function () {
            var now = moment_1.default();
            return (this._moment.year === now.year &&
                this._moment.month === now.month &&
                this._moment.date === now.date);
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(LocalDate.prototype, "dayOfMonth", {
        /**
         * Returns day of this date's month (i.e a number between 1 and 31)
         */
        get: function () {
            return this._moment.date();
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(LocalDate.prototype, "monthValue", {
        /**
         * Returns integer (Number) representing month in date
         * Starts at 1: i.e. January returns 1
         */
        get: function () {
            return this._moment.month();
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(LocalDate.prototype, "year", {
        /**
         * Returns numeric date year value - e.g. 2005
         */
        get: function () {
            return this._moment.year();
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(LocalDate.prototype, "timeReadable", {
        /**
         * Returns current time, in human-readable format: e.g. "1:24pm"
         */
        get: function () {
            return this._moment.format("h:mma");
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(LocalDate.prototype, "string", {
        /**
         * Returns date in numeric string format ("YYYY-MM-DD"): e.g. "2015-10-30"
         * Primarily for use in requests
         */
        get: function () {
            return this.toTimestampString
                ? this.toTimestampString()
                : this._moment.format("YYYY-MM-DD");
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(LocalDate.prototype, "mediumDateString", {
        /**
         * Returns localized medium date string: e.g. "Jan 1, 2000", "31 juil. 2018"
         */
        get: function () {
            return this._moment.format("ll");
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(LocalDate.prototype, "longDateString", {
        /**
         * Returns localized long date string: e.g. "January 1, 2000", "31 juillet 2018"
         */
        get: function () {
            return this._moment.format("LL");
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(LocalDate.prototype, "dayAndMonthString", {
        /**
         * Returns localized Day and month string (ex: Jan 1)
         */
        get: function () {
            return this._moment.format("LL").replace(/[ ,]*[0-9]{4}[ ,]*/g, "");
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(LocalDate.prototype, "monthAndYearString", {
        /**
         * Returns month and year string: e.g. "Jan 2000", "juil. 2018"
         * TODO make this work consistently, with language-based order taken care of
         */
        get: function () {
            var month = moment_1.default.localeData().monthsShort()[this._moment.month()];
            var year = this._moment.year();
            return month + " " + year;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(LocalDate.prototype, "fullCalendarDateTimeString", {
        /**
         * Returns date and time string in calendar format. e.g. "Today, 4:20 PM"
         */
        get: function () {
            return this._moment.calendar().replace(" at ", ", ");
        },
        enumerable: true,
        configurable: true
    });
    LocalDate.serialize = function (localDate) { return localDate._moment.toJSON(); };
    LocalDate.deserialize = function (dateData) { return LocalDate.new(moment_1.default(dateData)); };
    /**
     * Build new LocalDate using optional Moment or LocalDate object
     * Defaults to creating & using Moment object with current date & time
     *
     * @param {Moment|LocalDate} momentInstance Moment or LocalDate obj to build new LocalDate from
     * @return {LocalDate} New LocalDate object
     * @static
     * @constructor new {factory method}
     */
    LocalDate.new = function (dateObj) {
        return new LocalDate(dateObj);
    };
    return LocalDate;
}(javelin_exports_1.JLocalDate));
exports.LocalDate = LocalDate;
/******************************* BIND EXPECTED MANGLED METHOD NAMES *******************************/
// Still required due to overload of existing kotlin operator
shared_1.addImmutableProp(LocalDate.prototype, compareToHashedKey1, function compareTo(date) {
    return this["compareTo"](date);
});
shared_1.addImmutableProp(LocalDate.prototype, compareToHashedKey2, function compareTo(date) {
    return this["compareTo"](date);
});
/********************************** METADATA BINDING - LocalDate **********************************/
/**
 * Bind metadata from JLocalDate "interface" spit out by the transpiler
 */
Object.defineProperty(LocalDate, "$metadata$", {
    configurable: false,
    enumerable: false,
    writable: false,
    value: javelin_exports_1.JLocalDate.$metadata$,
});
/************************************** EXPORT ZonedDateTime **************************************/
/**
 * Javelin representation of a date and time with a timezone
 * @author Andrew Faulkner & Julien Guerinet
 * @since 2.4.30
 */
var ZonedDateTime = /** @class */ (function (_super) {
    __extends(ZonedDateTime, _super);
    /**
     * Build new ZonedDateTime using optional Moment, ZonedDateTime or LocalDate
     * object [dateObj] (Defaults to moment object with current date)
     */
    function ZonedDateTime(dateObj) {
        var _this = _super.call(this, dateObj) || this;
        _this.inspect = function () { return "{ ZonedDateTime ~ " + util_1.inspect(_this._moment) + " }"; };
        /**
         * Returns date as string in `2017-11-27T17:34:12.129Z` format
         */
        _this.toTimestampString = function () { return _this._moment.format("YYYY-MM-DDThh:mm:ss.SSSZ"); };
        /**
         * Returns date as string in `2017-11-27T17:34:12.129Z` format
         */
        _this.toJson = function () { return _this.toTimestampString(); };
        _this.usesTime = true;
        return _this;
    }
    ZonedDateTime.new = function (dateObj) {
        return new ZonedDateTime(dateObj);
    };
    return ZonedDateTime;
}(LocalDate));
exports.ZonedDateTime = ZonedDateTime;
/******************************** METADATA BINDING - ZonedDateTime ********************************/
/**
 * Bind metadata from JZonedDateTime "interface" spit out by the transpiler
 */
Reflect.defineProperty(ZonedDateTime, "$metadata$", {
    configurable: false,
    enumerable: false,
    writable: false,
    value: javelin_exports_1.javelin.data.model.date.JZonedDateTime,
});
//# sourceMappingURL=date-time-all.js.map